import dayjs from "dayjs"

/**
 * Transform backend crew profile data to match the table's expected format
 * @param {Object} profile - Backend profile object
 * @returns {Object} Transformed profile for table display
 */
export const transformCrewProfile = (profile) => {
  const {
    id,
    first_name,
    last_name,
    country_code,
    phone_number,
    availability,
    next_time_off,
    photo_url,
    email,
    role,
    assigned_to_count,
    assigned_experiences,
    upcoming_bookings_count,
    status,
    title,
    description,
    can_edit
  } = profile || {}

  // Build full name from first and last name
  const name = `${first_name || ""} ${last_name || ""}`.trim() || "Unknown"

  // Format phone with country code
  const phone = country_code && phone_number ? `${country_code} ${phone_number}` : phone_number || "-"

  return {
    key: id,
    id,
    image: photo_url,
    name,
    firstName: first_name,
    lastName: last_name,
    email: email || "-",
    role: role?.name || "-",
    roleId: role?.id,
    phone,
    phoneNumber: phone_number,
    phoneCountryCode: country_code || "US",
    title: title || "",
    description: description || "",
    // Both camelCase (for table display) and snake_case (for modal)
    assignedTo: Array.isArray(assigned_experiences) ? assigned_experiences.length : (assigned_to_count || 0),
    assigned_experiences: Array.isArray(assigned_experiences) ? assigned_experiences : [],
    upcomingBookings: upcoming_bookings_count || 0,
    availability: availability || "-",
    nextTimeOff: next_time_off || "-",
    status,
    can_edit: can_edit
  }
}

/**
 * Get availability status and next time off from availabilities array
 * @param {Array} availabilities - Array of availability objects from API
 * API structure: { availability_type, all_year, season_start, season_end, summary }
 * @returns {Object} { availability, nextTimeOff }
 */
const getAvailabilityInfo = (availabilities) => {
  if (!availabilities || availabilities.length === 0) {
    return {
      availability: "Available",
      nextTimeOff: "-"
    }
  }

  const now = dayjs()

  // Filter for unavailability periods (availability_type !== "available")
  const unavailabilities = availabilities.filter((avail) => avail.availability_type && avail.availability_type !== "available")

  // Find current unavailability (if any)
  const currentUnavailability = unavailabilities.find((avail) => {
    if (!avail.season_start) return false

    const startDate = dayjs(avail.season_start)
    const endDate = avail.season_end ? dayjs(avail.season_end) : startDate

    return now.isSameOrAfter(startDate, "day") && now.isSameOrBefore(endDate, "day")
  })

  if (currentUnavailability) {
    const endDate = currentUnavailability.season_end ? dayjs(currentUnavailability.season_end) : dayjs(currentUnavailability.season_start)

    return {
      availability: `Off till ${endDate.format("MM/DD/YYYY")}`,
      nextTimeOff: endDate.format("MM/DD/YYYY")
    }
  }

  // Find next future unavailability
  const futureUnavailabilities = unavailabilities
    .filter((avail) => avail.season_start && dayjs(avail.season_start).isAfter(now, "day"))
    .sort((a, b) => dayjs(a.season_start).diff(dayjs(b.season_start)))

  if (futureUnavailabilities.length > 0) {
    const nextOff = futureUnavailabilities[0]
    const startDate = dayjs(nextOff.season_start)

    return {
      availability: "Available",
      nextTimeOff: startDate.format("MM/DD/YYYY")
    }
  }

  return {
    availability: "Available",
    nextTimeOff: "-"
  }
}

/**
 * Transform array of backend profiles
 * @param {Array} profiles - Array of backend profile objects
 * @returns {Array} Transformed profiles for table display
 */
export const transformCrewProfiles = (profiles) => {
  if (!Array.isArray(profiles)) return []
  return profiles.map(transformCrewProfile)
}
